import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

// The Hestia theme adds custom font-size keys (text-h1/h2/display/body/meta +
// a numeric ramp). Vanilla tailwind-merge doesn't know these are font-sizes, so
// when a class list contains both a size and a text COLOUR — e.g. CardTitle's
// `text-h2 … text-[var(--ink-700)]` — it treats them as the same `text-` group
// and drops the earlier one, silently stripping the size off the heading.
// Register the keys so size and colour coexist.
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [
        {
          text: [
            'display',
            'h1',
            'h2',
            'body',
            'meta',
            '12',
            '14',
            '16',
            '18',
            '20',
            '24',
            '32',
            '40',
          ],
        },
      ],
    },
  },
});

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * True only when `url` parses to an absolute `http:`/`https:` URL.
 * Guards `href` attributes against `javascript:`, `data:`, and other
 * unsafe schemes — `rel="noreferrer noopener"` does NOT protect against
 * those, so AI-/editor-sourced URLs rendered on the public /approve page
 * must pass through this check before becoming a link.
 */
export function isSafeHttpUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Derive a URL-safe slug from a free-text name: lowercased, combining
 * diacritics stripped (NFD + U+0300–U+036F removal), non-alphanumerics
 * collapsed to single hyphens, trimmed, and capped at 64 chars. Used to
 * suggest a hotel slug from its display name.
 */
export function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}
